import React from 'react';
import { motion } from 'framer-motion';
import { MessageCircle } from 'lucide-react';
import { LEGAL_CONTACT_EMAIL } from '../data/legalTexts';

/**
 * Floating feedback pill, visible site-wide while ColoBongo is in early
 * access. Tapping it opens the user's mail client with subject and body
 * pre-filled (current page + user agent included for diagnostic).
 *
 * Designed to sit just above the BottomNav and not collide with the
 * central Scan FAB. Same mailto template as the SettingsPage feedback
 * card so both entry points feel consistent.
 */
export default function FeedbackPill({ language = 'fr', withBottomNav = true }) {
  const lang = language === 'en' ? 'en' : 'fr';

  const subjectFr = '[ColoBongo] Bug ou idée';
  const subjectEn = '[ColoBongo] Bug or idea';

  const bodyFr =
    "Coucou Bongo !\n\n"
    + "(Décris ici ton bug ou ton idée — n'hésite pas à donner des détails)\n\n\n\n"
    + "---\n"
    + "Pour t'aider à reproduire :\n"
    + `Page : ${typeof window !== 'undefined' ? window.location.href : ''}\n`
    + `Appareil : ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}\n`;

  const bodyEn =
    "Hi Bongo!\n\n"
    + "(Describe your bug or idea here — feel free to add details)\n\n\n\n"
    + "---\n"
    + "To help me reproduce:\n"
    + `Page: ${typeof window !== 'undefined' ? window.location.href : ''}\n`
    + `Device: ${typeof navigator !== 'undefined' ? navigator.userAgent : ''}\n`;

  const href =
    `mailto:${LEGAL_CONTACT_EMAIL}`
    + `?subject=${encodeURIComponent(lang === 'fr' ? subjectFr : subjectEn)}`
    + `&body=${encodeURIComponent(lang === 'fr' ? bodyFr : bodyEn)}`;

  const ariaLabel = lang === 'fr'
    ? 'Signaler un bug ou partager une idée'
    : 'Report a bug or share an idea';
  const earlyTag = lang === 'fr' ? 'Accès anticipé' : 'Early access';
  const ctaShort = lang === 'fr' ? 'Bug / Idée' : 'Bug / Idea';

  return (
    <motion.a
      href={href}
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, delay: 0.6 }}
      whileTap={{ scale: 0.96 }}
      aria-label={ariaLabel}
      title={ariaLabel}
      className={`fixed right-3 z-40 inline-flex items-center gap-2 px-3 py-2 rounded-full bg-white/95 backdrop-blur-md border border-[rgba(232,168,124,0.45)] shadow-[0_6px_18px_rgba(122,101,88,0.18)] text-xs font-semibold text-[color:var(--cb-text)] hover:bg-[rgba(255,248,240,0.95)] hover:border-[rgba(232,168,124,0.7)] transition-colors ${
        withBottomNav ? 'bottom-[88px]' : 'bottom-4'
      }`}
      data-testid="feedback-pill"
    >
      <span
        aria-hidden="true"
        className="hidden sm:inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide text-[color:var(--cb-primary)]"
      >
        <span className="inline-block w-1.5 h-1.5 rounded-full bg-[color:var(--cb-primary)]" />
        {earlyTag}
      </span>
      <span aria-hidden="true" className="hidden sm:inline-block w-px h-3 bg-[rgba(232,168,124,0.4)]" />
      <MessageCircle size={14} className="text-[color:var(--cb-primary)]" />
      <span>{ctaShort}</span>
    </motion.a>
  );
}
